import React, { createContext, useContext, useEffect, useState, useMemo } from 'react';
import { useAuth } from './AuthContext';

const PremiumContext = createContext();

export const usePremium = () => {
  const context = useContext(PremiumContext);
  if (!context) {
    throw new Error('usePremium must be used within a PremiumProvider');
  }
  return context;
};

export const PremiumProvider = ({ children }) => {
  const { currentUser, userPaymentData } = useAuth();
  const [premiumStatus, setPremiumStatus] = useState({
    isPremium: false,
    loading: true,
    expirationDate: null,
    paymentStatus: null
  });

  // Global premium validation logic
  const validatePremiumAccess = useMemo(() => {
    console.log('PremiumContext - Validating premium access for user data:', userPaymentData);
    
    if (!userPaymentData || typeof userPaymentData !== 'object') {
      console.log('PremiumContext - No user payment data found');
      return {
        isPremium: false,
        loading: false,
        expirationDate: null,
        paymentStatus: null
      };
    }

    try {
      // Be flexible with payment status validation
      const hasValidPayment = userPaymentData.paymentStatus === 'confirmed' || 
                             userPaymentData.paymentStatus === 'active' || 
                             userPaymentData.paymentStatus === 'paid' ||
                             userPaymentData.paymentStatus === true ||
                             userPaymentData.paymentConfirmed === true ||
                             userPaymentData.isPremium === true;

      console.log('PremiumContext - Payment status:', userPaymentData.paymentStatus, 'Valid payment:', hasValidPayment);

      // Check if expiration date exists and is valid
      if (!userPaymentData.expirationDate) {
        console.log('PremiumContext - No expiration date found');
        return {
          isPremium: false,
          loading: false,
          expirationDate: null,
          paymentStatus: userPaymentData.paymentStatus
        };
      }

      let expirationDate;

      // Handle Firebase Timestamp objects
      if (userPaymentData.expirationDate && typeof userPaymentData.expirationDate === 'object' && userPaymentData.expirationDate.toDate) {
        // Firebase Timestamp object
        expirationDate = userPaymentData.expirationDate.toDate();
        console.log('PremiumContext - Converted Firebase Timestamp to Date:', expirationDate);
      } else if (userPaymentData.expirationDate && typeof userPaymentData.expirationDate === 'object' && userPaymentData.expirationDate.seconds) {
        // Firebase Timestamp-like object with seconds
        expirationDate = new Date(userPaymentData.expirationDate.seconds * 1000);
        console.log('PremiumContext - Converted Timestamp seconds to Date:', expirationDate);
      } else {
        // Regular date string
        expirationDate = new Date(userPaymentData.expirationDate);
        console.log('PremiumContext - Created Date from string:', expirationDate);
      }

      const now = new Date();

      // Check if the expiration date is valid and in the future
      const hasValidExpiration = !isNaN(expirationDate.getTime()) && expirationDate > now;
      console.log('PremiumContext - Expiration date:', expirationDate, 'Current date:', now, 'Valid expiration:', hasValidExpiration);

      const isPremium = hasValidPayment && hasValidExpiration;
      console.log('PremiumContext - Final premium status:', isPremium);

      return {
        isPremium,
        loading: false,
        expirationDate,
        paymentStatus: userPaymentData.paymentStatus
      };
    } catch (error) {
      console.error('PremiumContext - Error validating premium access:', error);
      return {
        isPremium: false,
        loading: false,
        expirationDate: null,
        paymentStatus: userPaymentData.paymentStatus
      };
    }
  }, [userPaymentData]);

  // Update premium status when validation changes
  useEffect(() => {
    setPremiumStatus(validatePremiumAccess);
  }, [validatePremiumAccess]);

  // Helper function to get premium status info for display
  const getPremiumStatusInfo = () => {
    if (!userPaymentData || typeof userPaymentData !== 'object') {
      return { hasPremium: false, message: '' };
    }

    if (userPaymentData.paymentStatus !== 'confirmed') {
      return { hasPremium: false, message: 'Premium subscription not active' };
    }

    if (!userPaymentData.expirationDate) {
      return { hasPremium: false, message: 'No expiration date found' };
    }

    let expirationDate;
    try {
      if (userPaymentData.expirationDate && typeof userPaymentData.expirationDate === 'object' && userPaymentData.expirationDate.toDate) {
        expirationDate = userPaymentData.expirationDate.toDate();
      } else if (userPaymentData.expirationDate && typeof userPaymentData.expirationDate === 'object' && userPaymentData.expirationDate.seconds) {
        expirationDate = new Date(userPaymentData.expirationDate.seconds * 1000);
      } else {
        expirationDate = new Date(userPaymentData.expirationDate);
      }

      const now = new Date();

      if (isNaN(expirationDate.getTime())) {
        return { hasPremium: false, message: 'Invalid expiration date' };
      }

      if (expirationDate <= now) {
        return { hasPremium: false, message: `Premium expired on ${expirationDate.toLocaleDateString()}` };
      }

      return { 
        hasPremium: true, 
        message: `Premium active until ${expirationDate.toLocaleDateString()}` 
      };
    } catch (error) {
      return { hasPremium: false, message: 'Error checking premium status' };
    }
  };

  const value = {
    isPremium: premiumStatus.isPremium,
    loading: premiumStatus.loading,
    expirationDate: premiumStatus.expirationDate,
    paymentStatus: premiumStatus.paymentStatus,
    getPremiumStatusInfo
  };

  console.log('PremiumContext - Providing context value:', value);

  return (
    <PremiumContext.Provider value={value}>
      {children}
    </PremiumContext.Provider>
  );
};